import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { createPatient } from "@/lib/api";

const initialState = {
  mir_number: "",
  first_name: "",
  last_name: "",
  date_of_birth: "",
  gender: "male",
  admission_date: "",
  discharge_date: "",
  ward: "",
  attending_physician: "",
  status: "admitted",
};

export default function AddPatient() {
  const navigate = useNavigate();
  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);

  const set = (field) => (e) => {
    const val = typeof e === "string" ? e : e.target.value;
    setForm((prev) => ({ ...prev, [field]: val }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.mir_number || !form.first_name || !form.last_name || !form.date_of_birth || !form.admission_date) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        admission_date: new Date(form.admission_date).toISOString(),
        discharge_date: form.discharge_date ? new Date(form.discharge_date).toISOString() : null,
      };
      const res = await createPatient(payload);
      toast.success("Patient created successfully");
      navigate(`/patients/${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create patient");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6 animate-fade-in" data-testid="add-patient-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/")} data-testid="add-patient-back-btn">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-slate-900 tracking-tight" data-testid="add-patient-title">
            Add New Patient
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Enter patient admission details below</p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="bg-white border border-slate-100 rounded-xl p-6 sm:p-8 space-y-6" data-testid="add-patient-form">
        {/* MIR Number */}
        <div className="space-y-2">
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500" htmlFor="mir">MIR Number *</Label>
          <Input id="mir" placeholder="e.g. 2025001" value={form.mir_number} onChange={set("mir_number")} data-testid="input-mir" />
        </div>

        {/* Name */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500" htmlFor="fname">First Name *</Label>
            <Input id="fname" placeholder="John" value={form.first_name} onChange={set("first_name")} data-testid="input-first-name" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500" htmlFor="lname">Last Name *</Label>
            <Input id="lname" placeholder="Doe" value={form.last_name} onChange={set("last_name")} data-testid="input-last-name" />
          </div>
        </div>

        {/* DOB + Gender */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500" htmlFor="dob">Date of Birth *</Label>
            <Input id="dob" type="date" value={form.date_of_birth} onChange={set("date_of_birth")} data-testid="input-dob" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Gender *</Label>
            <Select value={form.gender} onValueChange={set("gender")}>
              <SelectTrigger data-testid="select-gender"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="male">Male</SelectItem>
                <SelectItem value="female">Female</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Admission + Discharge */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500" htmlFor="admit">Admission Date *</Label>
            <Input id="admit" type="date" value={form.admission_date} onChange={set("admission_date")} data-testid="input-admission-date" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500" htmlFor="discharge">Discharge Date</Label>
            <Input id="discharge" type="date" value={form.discharge_date} onChange={set("discharge_date")} data-testid="input-discharge-date" />
          </div>
        </div>

        {/* Ward + Physician */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500" htmlFor="ward">Ward</Label>
            <Input id="ward" placeholder="e.g. Cardiology A3" value={form.ward} onChange={set("ward")} data-testid="input-ward" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500" htmlFor="physician">Attending Physician</Label>
            <Input id="physician" placeholder="e.g. Sarah Chen" value={form.attending_physician} onChange={set("attending_physician")} data-testid="input-physician" />
          </div>
        </div>

        {/* Status */}
        <div className="space-y-2">
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Status</Label>
          <Select value={form.status} onValueChange={set("status")}>
            <SelectTrigger data-testid="select-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="admitted">Admitted</SelectItem>
              <SelectItem value="discharged">Discharged</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Submit */}
        <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
          <Button type="button" variant="outline" onClick={() => navigate("/")} data-testid="cancel-add-patient-btn">Cancel</Button>
          <Button
            type="submit"
            disabled={submitting}
            className="bg-slate-900 text-white hover:bg-slate-800 shadow-sm"
            data-testid="submit-patient-btn"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            {submitting ? "Creating..." : "Create Patient"}
          </Button>
        </div>
      </form>
    </div>
  );
}
